import React, { useState, useEffect } from 'react';
import { post } from '../../services/ApiService';

function StepEight({ formData, onNext }) {
    const [qualificationFiles, setQualificationFiles] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState(formData.qualificationFiles || []);
    const [error, setError] = useState('');
    const [uploadProgress, setUploadProgress] = useState({});
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    // Initialize from formData
    useEffect(() => {
        if (formData.qualificationFiles) {
            setUploadedFiles(formData.qualificationFiles);
        }
    }, [formData]);

    const handleContinue = () => {
        if (uploadedFiles.length === 0) {
            setError('Please upload at least one qualification certificate.');
            return;
        }

        setError('');
        onNext({ qualificationFiles: uploadedFiles });
    };

    const onUpload = async (file, index) => {
        const formDataObj = new FormData();
        formDataObj.append("file", file);
        
        try {
            setUploadProgress(prev => ({
                ...prev,
                [index]: 0
            }));

            // Simulate progress
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => ({
                    ...prev,
                    [index]: prev[index] < 90 ? prev[index] + 10 : 90
                }));
            }, 200);

            const res = await post("home/file-upload", formDataObj, true);
            console.log(res);
            clearInterval(progressInterval);
            setUploadProgress(prev => ({
                ...prev,
                [index]: 100
            }));
            
            // Small delay to show 100% completion
            await new Promise(resolve => setTimeout(resolve, 300));
            
            if (res.success) {
                const fileName = res.file;
                setUploadedFiles(prev => [...prev, fileName]);
                // Remove the file from qualificationFiles after successful upload
                setQualificationFiles(prev => prev.filter((_, i) => i !== index));
                return fileName;
            } else {
                throw new Error(res.message || "Upload failed");
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            setUploadProgress(prev => ({
                ...prev,
                [index]: 0
            }));
            throw error;
        }
    };

    const onRemove = async (fileName, index) => {
        if (!fileName) return;

        try {
            const res = await post("home/file-remove", { filename: fileName });
            if (res.success) {
                setUploadedFiles(prev => prev.filter((file, i) => i !== index));
            }
            return res;
        } catch (error) {
            console.error("Error in file removal:", error);
            throw error;
        }
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        // Add new files to qualificationFiles state
        const newFiles = files.map(file => ({
            file,
            id: Date.now() + Math.random() // Unique ID for each file
        }));
        
        setQualificationFiles(prev => [...prev, ...newFiles]);

        // Upload each file
        newFiles.forEach(async (fileObj, index) => {
            const globalIndex = qualificationFiles.length + index;
            try {
                await onUpload(fileObj.file, globalIndex);
            } catch (error) {
                setError(`Failed to upload ${fileObj.file.name}. Please try again.`);
                // Remove failed file from qualificationFiles
                setQualificationFiles(prev => prev.filter(f => f.id !== fileObj.id));
            }
        });
    };

    const removeLocalFile = (id) => {
        setQualificationFiles(prev => prev.filter(file => file.id !== id));
        // Also remove from upload progress
        setUploadProgress(prev => {
            const newProgress = { ...prev };
            Object.keys(newProgress).forEach(key => {
                if (key.startsWith(id)) {
                    delete newProgress[key];
                }
            });
            return newProgress;
        });
    };

    const renderFilePreview = (fileObj, index, isUploaded = false) => {
        const fileName = isUploaded ? fileObj : fileObj.file.name;
        const progress = uploadProgress[index] || 0;
        const isNewFile = !isUploaded;
        
        const isImage = isUploaded ? 
            /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName) : 
            fileObj.file.type.startsWith('image/');
        
        const previewURL = isUploaded ? 
            `${backendUrl}/uploads/${fileName}` : 
            URL.createObjectURL(fileObj.file);

        return (
            <div className="file_prev" key={isUploaded ? `uploaded-${index}` : `local-${fileObj.id}`}>
                <div className="d-flex align-items-center justify-content-between gap-3 mt-2">
                    <div className="d-flex align-items-center gap-2">
                        {isImage && (
                            <img
                                src={previewURL}
                                alt="preview"
                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        )}
                        <div>
                            <span>{fileName}</span>
                            {isUploaded && (
                                <div className="text-success small">
                                    Uploaded: {fileName}
                                </div>
                            )}
                            {isNewFile && progress === 0 && (
                                <div className="text-warning small">Ready to upload</div>
                            )}
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {progress > 0 && progress < 100 && (
                            <div className="progress" style={{ width: '100px', height: '8px' }}>
                                <div 
                                    className="progress-bar" 
                                    style={{ width: `${progress}%` }}
                                ></div>
                            </div>
                        )}
                        <button 
                            className="border-0 bg-transparent text-danger" 
                            onClick={() => isUploaded ? 
                                onRemove(fileName, index) : 
                                removeLocalFile(fileObj.id)
                            }
                            disabled={progress > 0 && progress < 100}
                        >
                            <img src="/assets/bin_icon.png" alt="delete" style={{ width: '15px' }} />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const isFormValid = uploadedFiles.length > 0;

    return (
        <div className="step-form">
            <h6 className="mb-4">Qualifications</h6>

            {error && <div className="alert alert-danger">{error}</div>}

            <p>
                Please upload a copy of your degree or training certificates relevant to the role you are applying for
                (Pharmacy Degree certificate, GPhC registration certificate, NVQ level 3 certificate). <span className="text-danger">*</span>
            </p>

            {/* Show upload input only when no files are being processed and no files are uploaded yet */}
            {(qualificationFiles.length === 0 && uploadedFiles.length === 0) && (
                <div className="mb-3">
                    <div className="upl_file">
                        <input
                            type="file"
                            className="form-control"
                            onChange={handleFileChange}
                            multiple
                        />
                        <p className="drag_text text-center">
                            Drag your file(s) here or <b>Browse</b>
                            <span className="d-block">Max 1.5mb file size</span>
                        </p>
                    </div>
                </div>
            )}

            {/* Show uploading files */}
            {qualificationFiles.map((fileObj, index) => 
                renderFilePreview(fileObj, index, false)
            )}

            {/* Show uploaded files */}
            {uploadedFiles.map((fileName, index) => 
                renderFilePreview(fileName, index, true)
            )}

            {/* Show "Add More Files" button when files are already uploaded */}
            {uploadedFiles.length > 0 && (
                <div className="mb-3 mt-3">
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                            // Trigger file input click
                            const fileInput = document.createElement('input');
                            fileInput.type = 'file';
                            fileInput.accept = '*/*';
                            fileInput.multiple = true;
                            fileInput.onchange = handleFileChange;
                            fileInput.click();
                        }}
                    >
                        Add More Files
                    </button>
                </div>
            )}

            <div className="text-end mt-5">
                <button
                    className="btn btn-dark px-4"
                    onClick={handleContinue}
                    disabled={!isFormValid}
                >
                    Next
                </button>
            </div>
        </div>
    );
}

export default StepEight;